export const PKG_ID = "TCR-2027-021";

export const PRODUCT_CLASS = "Diagnostic imaging workstation (synthetic product class)";

export const PRODUCT_CLASSES = [
    "Diagnostic-imaging workstation",
    "Telehealth endpoint",
    "Medical display",
    "Connected clinical device",
    "Medication-management device",
    "Specialist communications system",
    "Healthcare integration gateway",
];

export const PACKAGE_V1 = {
    id: PKG_ID,
    version: "1",
    productClass: PRODUCT_CLASS,
    currentVersion: "IWS 4.2.1 (synthetic)",
    proposedVersion: "IWS 4.3.0 (synthetic)",
    securityClassification: "HIGH — includes security remediation",
    changeContents: [
        "Apply a security update",
        "Update the application version",
        "Change one interoperability configuration",
        "Introduce a revised post-installation health check",
        "Withdraw support for one legacy operating-system version",
    ],
    compatibility: [
        "Requires OS build 2024-R2 or later",
        "Legacy OS 2019-LTS no longer supported",
        "Interoperability interface profile v2 required",
    ],
    installMethod: "Authorised engineer, on-site staged installation",
    postInstallTests: [
        "Security health check",
        "Application version confirmation",
        "Interoperability interface test",
        "Revised post-installation health check",
    ],
    rollbackConditions: "Verified rollback package must be available on site before installation begins",
    proposedWindow: "Common release date 2027-09-14 (synthetic)",
    supplierSignature: "mfr-sig:4e8a…b73c (synthetic)",
    packageRef: "sha256:d02f…91aa (synthetic immutable reference)",
};

export const PACKAGE_V2_REVISIONS = [
    "Add explicit legacy-device exclusion",
    "Clarify the rollback test case",
    "Support a staged rather than common release",
    "Provide an additional interoperability test",
    "Extend the permitted implementation period",
];

export const ROLLOUT_PLAN = [
    "Pilot site first",
    "Two-site controlled cohort",
    "Remaining compatible installations after evidence review",
    "Legacy device held outside release",
    "Engineer briefing required before assignment",
    "Revised service window agreed with each provider",
];

export const ROLLOUT_CHAIN = [
    "IMPACT ASSESSED",
    "CHANGE CLARIFIED",
    "ROLLOUT PLAN PROPOSED",
    "LOCAL CONDITIONS ACCEPTED",
    "READY FOR BOUNDED DEPLOYMENT",
];

export const NODES = [
    {
        id: "mfr",
        name: "Product Manufacturer",
        short: "Manufacturer",
        desc: "Publishes and controls the authoritative product change.",
        holds: [
            "Product definition",
            "Software package",
            "Release notes",
            "Known risks",
            "Installation instructions",
            "Compatibility matrix",
            "Supplier test evidence",
            "Rollback package",
        ],
        may: ["Publish", "Clarify", "Replace", "Suspend", "Withdraw a product release"],
        mayNot: [
            "Declare an individual healthcare site operationally ready",
            "Approve hospital downtime",
            "Release equipment into clinical service",
            "Inspect unrestricted provider systems",
        ],
    },
    {
        id: "partner",
        name: "Healthcare Technology Partner",
        short: "Technology Partner",
        desc: "A distributor, integrator and service organisation operating between product manufacturers and healthcare providers.",
        holds: [
            "Customer and site relationships",
            "Installed-base records",
            "Service-contract scope",
            "Engineer capability and availability",
            "Installation history",
            "Integration dependencies",
            "Service desk and maintenance information",
        ],
        may: [
            "Assess delivery impact",
            "Identify affected installations",
            "Propose a rollout plan",
            "Assign an authorised engineer",
            "Confirm service readiness",
            "Complete installation evidence",
            "Raise supplier or provider exceptions",
        ],
        mayNot: [
            "Rewrite the manufacturer's product definition",
            "Approve provider clinical risk",
            "Release equipment into service without provider acceptance",
        ],
    },
    {
        id: "provider",
        name: "Provider Clinical Engineering and IT",
        short: "Provider Technical",
        desc: "Controls the local technical environment and installation permission.",
        holds: [
            "Local asset record",
            "Network and integration configuration",
            "Cybersecurity requirements",
            "Technical change calendar",
            "Local validation requirements",
            "Local infrastructure dependencies",
        ],
        may: [
            "Assess technical readiness",
            "Permit a bounded installation",
            "Accept or reject technical test evidence",
            "Require remediation",
            "Approve connection to the local environment",
        ],
        mayNot: [
            "Issue the manufacturer's release",
            "Determine clinical service availability alone",
            "Certify a supplier product globally",
        ],
    },
    {
        id: "clinical",
        name: "Clinical Service Owner",
        short: "Clinical Service",
        desc: "Controls release into the clinical operating environment.",
        holds: [
            "Operational service requirements",
            "Permitted downtime",
            "Clinical workflow dependencies",
            "Contingency arrangements",
            "Local release window",
            "Service acceptance criteria",
        ],
        may: [
            "Accept a deployment window",
            "Require a clinical workaround",
            "Postpone release",
            "Confirm operational acceptance",
            "Release the equipment back into clinical use",
        ],
        mayNot: [
            "Perform supplier engineering validation",
            "Alter the software package",
            "Waive mandatory technical controls without the appropriate authority",
        ],
    },
    {
        id: "assurance",
        name: "Quality and Assurance Role",
        short: "Assurance",
        desc: "A separately accountable quality or joint release-assurance function.",
        holds: [
            "Applicable quality policy",
            "Release checklist",
            "Required evidence classes",
            "Unresolved non-conformities",
            "Audit and assurance requirements",
        ],
        may: [
            "Confirm that required receipts are present",
            "Identify missing assurance evidence",
            "Accept a bounded deviation where authorised",
            "Close the release record",
        ],
        mayNot: [
            "Make the underlying supplier, engineering or clinical decisions on behalf of the other roles",
        ],
    },
];

export const PARTICIPANTS = NODES.filter((n) => n.id !== "mfr");

export const IMPACTS = {
    partner: {
        result: "AMBER — INSTALLED-BASE DEPENDENCIES",
        tone: "amber",
        findings: [
            "18 synthetic installations identified",
            "15 appear compatible",
            "2 require local confirmation",
            "1 legacy workstation is incompatible",
            "Engineer briefing not complete",
            "Staged rollout recommended",
        ],
        meta: {
            localSystem: "Service-management system (generic)",
            policyVersion: "HTP-SVC v6.2",
            unresolvedQuestions: 2,
            readinessState: "CONDITIONAL",
        },
    },
    provider: {
        result: "AMBER — NETWORK CHANGE REQUIRED",
        tone: "amber",
        findings: [
            "Security review passed",
            "Interface endpoint requires configuration",
            "Local test environment available",
            "Production connection not yet approved",
            "Rollback route confirmed",
        ],
        meta: {
            localSystem: "Hospital asset-management and ITSM platforms (generic)",
            policyVersion: "PRV-TEC v11.0",
            unresolvedQuestions: 1,
            readinessState: "CONDITIONAL",
        },
    },
    clinical: {
        result: "RED — PROPOSED DOWNTIME NOT ACCEPTABLE",
        tone: "red",
        findings: [
            "Proposed installation conflicts with a critical service window",
            "No validated clinical fallback exists during that period",
            "Alternative evening window available",
            "Local service owner approval required",
        ],
        meta: {
            localSystem: "Clinical service schedule (generic)",
            policyVersion: "CSO-OPS v4.7",
            unresolvedQuestions: 1,
            readinessState: "NOT READY",
        },
    },
    assurance: {
        result: "AMBER — ACCEPTANCE EVIDENCE INCOMPLETE",
        tone: "amber",
        findings: [
            "Supplier package received",
            "Installation checklist available",
            "Engineer competency receipt absent",
            "Clinical acceptance template available",
            "One rollback test case requires clarification",
        ],
        meta: {
            localSystem: "Quality-management system (generic)",
            policyVersion: "QAR-REL v3.1",
            unresolvedQuestions: 1,
            readinessState: "CONDITIONAL",
        },
    },
};

export const IMPACT_SUMMARY = [
    { label: "AMBER", count: 3, tone: "amber" },
    { label: "RED", count: 1, tone: "red" },
    { label: "LEGACY EXCLUSION", count: 1, tone: "slate" },
    { label: "CLARIFICATIONS", count: 5, tone: "slate" },
];

export const ENGINEER_CHECKS = [
    "Engineer role",
    "Briefing complete",
    "Applicable product package",
    "Site approval",
    "Permitted window",
    "Rollback availability",
];

export const PROVIDER_CHECKS = [
    "Change reference",
    "Local environment",
    "Integration configuration",
    "Backup state",
    "Test plan",
    "Rollback conditions",
];

export const INSTALL_RESULTS_BLOCKED = [
    ["Package installed", "PASS", "green"],
    ["Version confirmed", "IWS 4.3.0", "green"],
    ["Security health check", "PASS", "green"],
    ["Interoperability interface test", "FAIL", "red"],
    ["Production release", "BLOCKED", "red"],
];

export const INSTALL_RESULTS_CLEAN = [
    ["Package installed", "PASS", "green"],
    ["Version confirmed", "IWS 4.3.0", "green"],
    ["Security health check", "PASS", "green"],
    ["Interoperability interface test", "PASS", "green"],
    ["Revised post-installation health check", "PASS", "green"],
];

export const EXCEPTION_CHAIN = [
    "EXCEPTION ASSIGNED",
    "CONFIGURATION CORRECTED",
    "RECHECKING",
    "TECHNICALLY ACCEPTED",
];

export const CLINICAL_REVIEW = [
    "Technical acceptance",
    "Agreed service window",
    "Equipment availability",
    "Operational check",
    "Contingency state",
];

export const OBSERVATION_OUTCOMES = [
    "Technical health checks passed",
    "No service outage",
    "One configuration clarification recorded",
    "Pilot evidence accepted",
    "Wider rollout remains conditional",
    "Legacy-device replacement decision remains open",
];

export const MFR_RECEIVES = [
    "Affected product class",
    "Change-package version",
    "Exception category",
    "Test that failed",
    "Resolution classification",
    "Whether documentation clarification is proposed",
];

export const MFR_DOES_NOT_RECEIVE = [
    "Hospital network information",
    "Clinical activity",
    "Patient records",
    "Internal provider policies",
    "Full service-management logs",
];

export const OUTCOMES = [
    {
        state: "BLOCKED",
        tone: "amber",
        example: "BLOCKED — INTERFACE TEST FAILED",
        desc: "The release cannot proceed until a correctable condition is resolved. Correction requires a new check, not manual status editing.",
    },
    {
        state: "DENIED",
        tone: "red",
        example: "DENIED — NO ACCEPTABLE CLINICAL CONTINGENCY",
        desc: "An authorised role determines that the proposed release must not proceed at that site or time.",
    },
    {
        state: "ROLLED BACK",
        tone: "red",
        example: "ROLLED BACK — POST-INSTALLATION HEALTH CHECK FAILED",
        desc: "A previously installed release is withdrawn under the approved rollback procedure.",
    },
    {
        state: "EXCLUDED",
        tone: "slate",
        example: "EXCLUDED — LEGACY DEVICE NOT SUPPORTED",
        desc: "A product or site is outside the applicable change package.",
    },
    {
        state: "RELEASED",
        tone: "green",
        example: "RELEASED TO SERVICE",
        desc: "All required local decisions have been completed by the accountable roles.",
    },
];

export const GATES = [
    "Authoritative change package",
    "Applicability to the installed product",
    "Local technical readiness",
    "Authorised engineer assignment",
    "Supplier instruction version",
    "Provider installation permission",
    "Permitted service window",
    "Rollback readiness",
    "Technical test completion",
    "Clinical or operational acceptance",
    "Quality evidence completeness",
    "Release-to-service authority",
];

export const AI_MAY = [
    "Summarise",
    "Compare versions",
    "Identify possible dependencies",
    "Recommend an action",
    "Draft an exception",
    "Explain a state",
];

export const AI_MAY_NOT = [
    "Publish the supplier change",
    "Approve hospital downtime",
    "Authorise an engineer",
    "Waive a safety requirement",
    "Release equipment into service",
    "Close a failed test",
    "Override another role",
];

export const AI_LINE = "AI may analyse and recommend. Accountable roles must authorise and commit.";

export const STANDARDS = [
    ["ISO 9001", "Organisational quality management"],
    ["ISO 13485", "Medical-device quality management"],
    ["ISO 14971", "Medical-device risk management"],
    ["ISO 81001-1", "Health-software and health-IT safety, effectiveness and security responsibilities"],
    ["IEC 80001-1", "Risk management for connected medical devices and health software"],
    ["ISO 22301", "Business continuity"],
];

export const STANDARDS_LINE =
    "Standards shape the control requirements. Accountable organisations retain interpretation, implementation and release authority.";

export const STANDARDS_NOT_CLAIMED = [
    "ISO certification",
    "Automated conformance determination",
    "Authority to interpret ISO requirements",
    "Authority to approve a medical device",
    "Authority to release a product into all healthcare environments",
];

export const SCENARIOS = [
    { key: "blocked", label: "Interface test blocked and corrected" },
    { key: "success", label: "Successful controlled release" },
    { key: "denied", label: "Clinical downtime denied" },
    { key: "excluded", label: "Legacy device excluded" },
    { key: "rollback", label: "Post-installation failure and rollback" },
];

export const VIEWS = [
    { key: "network", label: "Network view" },
    { key: "mfr", label: "Manufacturer view" },
    { key: "partner", label: "Technology Partner view" },
    { key: "provider", label: "Provider Technical view" },
    { key: "clinical", label: "Clinical Service view" },
    { key: "assurance", label: "Assurance view" },
];

export const ROLE_VIEWS = {
    mfr: {
        evidence: ["Software package", "Compatibility matrix", "Supplier test evidence", "Rollback package"],
        received: ["Bounded impact receipts", "Exception category and failed test", "Resolution classification"],
        checks: ["Package integrity", "Version supersession", "Legacy exclusion applicability"],
        permitted: ["Publish", "Clarify", "Replace", "Suspend", "Withdraw"],
        decision: `Issued ${PKG_ID} version 2 with legacy exclusion and staged release`,
        receipt: "Change publication receipt",
    },
    partner: {
        evidence: ["Installed-base records", "Engineer capability and availability", "Service-contract scope", "Installation history"],
        received: ["Authoritative change package", "Provider window constraints", "Exception assignments"],
        checks: ["Installed-base applicability", "Engineer briefing status", "Rollout sequencing"],
        permitted: ["Assess delivery impact", "Propose rollout plan", "Assign authorised engineer", "Complete installation evidence"],
        decision: "Proposed a bounded staged deployment; assigned a briefed engineer to the pilot site",
        receipt: "Local impact receipt · Rollout-plan receipt · Installation-authority receipt",
    },
    provider: {
        evidence: ["Local asset record", "Network and integration configuration", "Cybersecurity requirements", "Technical change calendar"],
        received: ["Change package reference", "Installation-authority receipt", "Interface test results"],
        checks: ["Security review", "Interface endpoint configuration", "Backup state", "Rollback conditions"],
        permitted: ["Permit a bounded installation", "Accept or reject technical test evidence", "Require remediation"],
        decision: "Permitted bounded installation; blocked release on failed interface test until recheck passed",
        receipt: "Local impact receipt · Technical acceptance within the release receipt",
    },
    clinical: {
        evidence: ["Permitted downtime", "Clinical workflow dependencies", "Contingency arrangements", "Service acceptance criteria"],
        received: ["Technical acceptance", "Agreed service window", "Contingency state"],
        checks: ["Operational check", "Equipment availability", "Contingency validity"],
        permitted: ["Accept a deployment window", "Postpone release", "Confirm operational acceptance", "Release to service"],
        decision: "Rejected the proposed common window; accepted the alternative evening window; released the pilot into service",
        receipt: "Technical and operational release receipt",
    },
    assurance: {
        evidence: ["Quality policy", "Release checklist", "Required evidence classes", "Unresolved non-conformities"],
        received: ["Journey receipts from all roles", "Engineer competency receipt (initially absent)", "Rollback test clarification"],
        checks: ["Receipt completeness", "Evidence-class coverage", "Deviation authorisation"],
        permitted: ["Confirm required receipts present", "Identify missing assurance evidence", "Close the release record"],
        decision: "Held the record open until the engineer competency receipt arrived; closed the pilot release record",
        receipt: "Runtime-feedback receipt countersigned into the closed release record",
    },
};

export const VIEW_NOTES = {
    network: "Network view — all five independently operated role nodes and the bounded receipts moving between them.",
    mfr: "Manufacturer view — the supplier sees bounded impact and feedback receipts only. It cannot inspect provider systems or release equipment into clinical service.",
    partner: "Technology Partner view — installed-base knowledge, engineer authority and delivery readiness under local custody. Other organisations appear only as bounded receipt counterparties.",
    provider: "Provider Technical view — local technical environment, installation permission and test acceptance. Supplier and partner evidence is received only as bounded receipts.",
    clinical: "Clinical Service view — service windows, contingency and release-to-service authority. No engineering or supplier evidence is held here beyond bounded receipts.",
    assurance: "Assurance view — receipt completeness and release-record closure. This role does not remake the underlying supplier, engineering or clinical decisions.",
};

export const DOWNSTREAM = [
    "SUPPLIER CHANGE",
    "LOCAL IMPACT ASSESSMENT",
    "CLARIFICATION AND ROLLOUT PLAN",
    "AUTHORISED INSTALLATION",
    "TECHNICAL AND OPERATIONAL RELEASE",
];

export const UPSTREAM = [
    "POST-RELEASE OUTCOMES",
    "EXCEPTION RECEIPTS",
    "LOCAL DELIVERY FRICTION",
    "READINESS EVIDENCE",
    "SUPPLIER CLARIFICATION",
];

export const CLOSING_CHAIN = ["PUBLISH", "ASSESS", "PLAN", "INSTALL", "VERIFY", "RELEASE", "OBSERVE"];

export const KEY_LINE =
    "The supplier controls the product change. The service partner controls delivery readiness. The provider controls release into its clinical environment. KATLAS governs the journey between them.";

export const FINAL_MESSAGE =
    "A change is not complete when the supplier publishes it. It is complete when each accountable organisation has performed and evidenced its authorised part of the release.";

export const DISCLAIMERS = [
    "This is a synthetic demonstration. All dates, identifiers and organisations are illustrative.",
    "No patient data, clinical records or live hospital systems are used.",
    "No real supplier documents, device serial numbers or engineer identities appear.",
    "KATLAS does not certify products, interpret ISO requirements or approve medical devices.",
    "Accountable organisations retain interpretation, implementation and release authority.",
    "KATLAS checks, gates, routes, verifies and records declared state transitions and issues attributable receipts.",
];

const base = () => ({
    demonstration: "SIMULATED",
    synthetic: true,
    generated_at: "2027-06-01T00:00:00Z (illustrative)",
});

export const RECEIPTS = [
    {
        key: "publication",
        name: "Change publication receipt",
        proves: [
            "Manufacturer role",
            "Package identity",
            "Applicable product",
            "Version",
            "Publication status",
            "Proposed implementation period",
        ],
        json: {
            ...base(),
            receipt_type: "change_publication",
            issuing_role: "Product Manufacturer",
            change_package: PKG_ID,
            package_version: "2",
            applicable_product_class: PRODUCT_CLASS,
            software_version: "IWS 4.2.1 → IWS 4.3.0 (synthetic)",
            publication_status: "PUBLISHED — supersedes version 1",
            legacy_exclusion: "Legacy OS 2019-LTS workstations excluded",
            proposed_implementation_period: "2027-09-01 to 2027-12-15 (synthetic, extended)",
            previous_state: "CHANGE CLARIFIED",
            resulting_state: "CHANGE PUBLISHED (v2)",
            evidence_references: ["supplier-test-evidence:ref-2201 (synthetic)", "rollback-package:ref-2202 (synthetic)"],
            exceptions: [],
            supplier_signature: "mfr-sig:4e8a…b73c (synthetic)",
            verification_status: "SYNTHETIC — verified within the demonstration only",
        },
    },
    {
        key: "impact",
        name: "Local impact receipt",
        proves: [
            "Assessing organisation",
            "Local role",
            "Readiness state",
            "Dependencies",
            "Unresolved questions",
            "No disclosure of the underlying private records",
        ],
        json: {
            ...base(),
            receipt_type: "local_impact",
            issuing_role: "Clinical Service Owner",
            change_package: `${PKG_ID} v1`,
            local_policy_version: "CSO-OPS v4.7",
            readiness_state: "RED — PROPOSED DOWNTIME NOT ACCEPTABLE",
            dependencies: ["Alternative evening service window", "Validated clinical fallback"],
            unresolved_questions: 1,
            disclosure_boundary: "Raw service schedules and clinical workflow records remain under local custody",
            previous_state: "CHANGE PUBLISHED",
            resulting_state: "IMPACT ASSESSED (clinical)",
            evidence_references: ["service-window-review:ref-5510 (synthetic)"],
            role_signature: "cso-sig:91ff…03ba (synthetic)",
            verification_status: "SYNTHETIC — verified within the demonstration only",
        },
    },
    {
        key: "rollout",
        name: "Rollout-plan receipt",
        proves: [
            "Applicable sites or classes",
            "Exclusions",
            "Sequence",
            "Conditions",
            "Responsible parties",
            "Accepted transition window",
        ],
        json: {
            ...base(),
            receipt_type: "rollout_plan",
            issuing_role: "Healthcare Technology Partner",
            change_package: `${PKG_ID} v2`,
            applicable_installations: "17 of 18 synthetic installations",
            exclusions: ["1 legacy workstation — EXCLUDED, LEGACY DEVICE NOT SUPPORTED"],
            sequence: ["Pilot site", "Two-site controlled cohort", "Remaining compatible installations after evidence review"],
            conditions: ["Engineer briefing required before assignment", "Revised service window agreed with each provider"],
            responsible_parties: {
                delivery: "Healthcare Technology Partner",
                installation_permission: "Provider Clinical Engineering and IT",
                release_to_service: "Clinical Service Owner",
            },
            accepted_transition_window: "2027-09-01 to 2027-12-15 (synthetic)",
            previous_state: "ROLLOUT PLAN PROPOSED",
            resulting_state: "LOCAL CONDITIONS ACCEPTED",
            role_signature: "htp-sig:7c2d…e841 (synthetic)",
            verification_status: "SYNTHETIC — verified within the demonstration only",
        },
    },
    {
        key: "authority",
        name: "Installation-authority receipt",
        proves: [
            "Authorised engineer",
            "Correct site",
            "Current package",
            "Accepted window",
            "Required rollback state",
        ],
        json: {
            ...base(),
            receipt_type: "installation_authority",
            issuing_role: "Healthcare Technology Partner",
            engineer_reference: "ENG-SYN-014 (synthetic — briefing receipt BRF-2027-081 present)",
            site_reference: "PILOT-SITE-01 (synthetic)",
            change_package: `${PKG_ID} v2`,
            accepted_window: "2027-09-18 evening window (synthetic, provider-agreed)",
            rollback_state: "Verified rollback package staged on site",
            checks_passed: ["Engineer role", "Briefing complete", "Applicable product package", "Site approval", "Permitted window", "Rollback availability"],
            previous_state: "READY FOR BOUNDED DEPLOYMENT",
            resulting_state: "INSTALLATION AUTHORISED",
            role_signature: "htp-sig:aa19…40cf (synthetic)",
            verification_status: "SYNTHETIC — verified within the demonstration only",
        },
    },
    {
        key: "release",
        name: "Technical and operational release receipt",
        proves: [
            "Tests completed",
            "Exception history",
            "Technical acceptance",
            "Clinical-service acceptance",
            "Final release decision",
        ],
        json: {
            ...base(),
            receipt_type: "technical_operational_release",
            issuing_roles: ["Provider Clinical Engineering and IT", "Clinical Service Owner"],
            change_package: `${PKG_ID} v2`,
            site_reference: "PILOT-SITE-01 (synthetic)",
            tests_completed: [
                "Security health check — PASS",
                "Application version confirmation — PASS",
                "Interoperability interface test — FAIL then PASS on recheck",
                "Revised post-installation health check — PASS",
            ],
            exception_history: [
                {
                    state: "BLOCKED — INTERFACE TEST FAILED",
                    assigned_to: ["Healthcare Technology Partner", "Provider Clinical Engineering and IT"],
                    resolution: "CONFIGURATION CORRECTED → RECHECKING → TECHNICALLY ACCEPTED",
                },
            ],
            technical_acceptance: "TECHNICALLY ACCEPTED (Provider Clinical Engineering and IT)",
            clinical_service_acceptance: "Operational acceptance confirmed within agreed evening window",
            final_release_decision: "RELEASED TO SERVICE (Clinical Service Owner)",
            previous_state: "TECHNICALLY ACCEPTED",
            resulting_state: "RELEASED TO SERVICE",
            role_signatures: ["prv-sig:3d0b…77e2 (synthetic)", "cso-sig:c4a8…19fd (synthetic)"],
            verification_status: "SYNTHETIC — verified within the demonstration only",
        },
    },
    {
        key: "feedback",
        name: "Runtime-feedback receipt",
        proves: [
            "Post-release observation",
            "Exception category",
            "Resolution",
            "Documentation or product feedback",
            "Next rollout state",
        ],
        json: {
            ...base(),
            receipt_type: "runtime_feedback",
            issuing_role: "Quality and Assurance Role",
            change_package: `${PKG_ID} v2`,
            affected_product_class: PRODUCT_CLASS,
            observation_period: "14 synthetic days post release",
            observation_summary: ["Technical health checks passed", "No service outage", "One configuration clarification recorded"],
            exception_category: "Interoperability configuration",
            failed_test: "Interoperability interface test (pilot installation)",
            resolution_classification: "Corrected and rechecked — no recurrence",
            documentation_feedback: "Clarification of interface configuration step proposed to the manufacturer",
            manufacturer_receives: ["Product class", "Package version", "Exception category", "Failed test", "Resolution classification", "Documentation proposal"],
            manufacturer_does_not_receive: ["Hospital network information", "Clinical activity", "Patient records", "Internal provider policies", "Full service-management logs"],
            next_rollout_state: "PILOT VERIFIED — CONTROLLED SCALE AVAILABLE",
            role_signature: "qar-sig:60de…b112 (synthetic)",
            verification_status: "SYNTHETIC — verified within the demonstration only",
        },
    },
];

export const DIAG_CHECKS = [
    ["Role nodes loaded", "5 of 5 — Manufacturer, Technology Partner, Provider Technical, Clinical Service, Assurance", "pass"],
    ["Scenario data available", "5 deterministic scenarios (static synthetic data)", "pass"],
    ["Policy versions loaded", "HTP-SVC v6.2 · PRV-TEC v11.0 · CSO-OPS v4.7 · QAR-REL v3.1", "pass"],
    ["Receipt templates loaded", "6 of 6 journey receipt types", "pass"],
    ["State machine valid", "BLOCKED / DENIED / EXCLUDED / ROLLED BACK / RELEASED reachable", "pass"],
    ["Live node connection", "Disabled — Live node connection not configured", "off"],
    ["Patient data present", "None — synthetic data only", "pass"],
    ["External APIs configured", "None", "pass"],
];

export const downloadJson = (obj, filename) => {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};
